// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { grpc } from "../../../app/rpc/executor"
import { showSnackBar } from "../../snackbar/actions"
import { timesheetFieldClient } from "../../../app/rpc/client"
import * as Actions from "../actions"
import * as Slice from "../slice"
import * as TimelineActions from "../../timeline/actions"
import {
  EnableTimesheetFieldOptionsRequest,
  ListTimesheetFieldsRequest,
} from "../../../generated/wisetime/site/timesheetfield/v1/timesheet_field_pb"
import { Empty } from "google-protobuf/google/protobuf/empty_pb"

type EnableTimesheetFieldRpc = (request: EnableTimesheetFieldOptionsRequest) => Observable<Empty>

export const enableTimesheetFieldOptions = (enableTimesheetFieldOptionsRpc: EnableTimesheetFieldRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.enableTimesheetFieldOptions.match),
      map(action => action.payload),
      mergeMap(payload =>
        enableTimesheetFieldOptionsRpc(payload).pipe(
          mergeMap(() => of(Slice.enableTimesheetFieldOptionsFulfilled(payload.toObject()))),
          catchError(e => handleError(e, payload.getSiteId())),
        )
      ),
    )

const handleError = (e: unknown, siteId: string): Observable<Action> => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat([
        TimelineActions.listTimesheetFields({
          request: new ListTimesheetFieldsRequest().setSiteId(siteId),
          includeOptions: true,
        }),
        showSnackBar({
          message: t({
            id: "msg_timesheet_field_not_found",
            message: "Timesheet field not found.",
          }),
          messageType: "ERROR",
        }),
      ])
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({
          id: "msg_enable_timesheet_field_option_permission_denied",
          message: "Failed to enable timesheet field option. You do not have permission.",
        }),
        messageType: "ERROR",
      }))
    }
  }
  return of(showSnackBar({
    message: t({
      id: "msg_enable_timesheet_field_option_failed",
      message: "Failed to enable timesheet field option. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
    }),
    messageType: "ERROR",
  }))
}

const enableTimesheetFieldOptionsRpc = (request: EnableTimesheetFieldOptionsRequest): Observable<Empty> =>
  grpc(() => timesheetFieldClient.enableTimesheetFieldOptions(request, null))

export default enableTimesheetFieldOptions(enableTimesheetFieldOptionsRpc)
